import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { animate, stagger } from "animejs";
import { DemoKeyContext, useResolvedDemoKey } from "../../lib/demoKey";
import { GATEWAY, STATS, UPDATED, getNetwork } from "../../data/apiReference";
import { StarField, Meteors, FloatingOrb } from "../../Components/motion/space-primitives";
import Footer from "../../Components/rebrand/Footer";
import ApiSidebar from "../../Components/api/ApiSidebar";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Shell for /networks and /networks/:slug — hero band, left sidebar, content outlet.
const ApiLayout = () => {
  const keyState = useResolvedDemoKey();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Page title per route.
  useEffect(() => {
    const prev = document.title;
    const slug = location.pathname.split("/")[2];
    const name = slug ? getNetwork(slug)?.network.fullName || slug : null;
    document.title = name ? `${name} · Networks Reference | Nodefleet` : "Networks Reference | Nodefleet";
    return () => {
      document.title = prev;
    };
  }, [location.pathname]);

  // Staggered reveal of content blocks on every route change (anime.js).
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const frame = requestAnimationFrame(() => {
      // Only the first screenful animates — long pages (70+ blocks) must not
      // keep content invisible for seconds; everything below stays visible.
      const targets = Array.from(document.querySelectorAll("main .doc-reveal")).slice(0, 18);
      if (!targets.length) return;
      animate(targets, {
        opacity: [0, 1],
        translateY: [10, 0],
        duration: 460,
        delay: stagger(30, { start: 30 }),
        ease: "outCubic",
        composition: "replace",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [location.pathname]);

  return (
    <DemoKeyContext.Provider value={keyState}>
      <div className="relative -mt-24 min-h-screen bg-space text-white">
        {/* Hero band */}
        <div className="relative overflow-hidden border-b border-white/10 bg-space-deep pb-10 pt-36 max-sm:pt-28">
          <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
            <div className="bg-drift absolute inset-y-0 left-0 flex h-full" style={{ width: "200%", animationDuration: "100s" }}>
              <div className="relative h-full w-1/2 shrink-0">
                <StarField count={50} />
              </div>
              <div className="relative h-full w-1/2 shrink-0">
                <StarField count={50} />
              </div>
            </div>
          </div>
          <Meteors count={2} className="z-0 opacity-60" />
          <FloatingOrb size={420} color="rgba(90, 169, 230, 0.16)" style={{ top: -160, right: "6%" }} />
          <FloatingOrb size={300} color="rgba(153, 223, 175, 0.14)" style={{ bottom: -120, left: "-4%" }} />

          <div className="relative z-10 mx-auto flex max-w-[1400px] flex-wrap items-end justify-between gap-6 px-8 max-sm:px-6 lg:px-12">
            <div>
              <Link to="/" className="text-xs font-semibold uppercase tracking-[0.16em] text-primary no-underline hover:underline">
                ← Nodefleet
              </Link>
              <h1 className="mb-2 mt-3 text-4xl font-bold leading-tight max-sm:text-3xl">
                Networks <span className="text-brand-gradient italic">Reference</span>
              </h1>
              <p className="m-0 max-w-2xl text-gray-400">
                {STATS.networks} networks by chain id, and what Nodefleet runs on each — Datanodes RPC over JSON-RPC, GraphQL, gRPC, REST
                and WebSocket, Nodegate stacks and Chain Graph metrics.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 font-mono text-gray-300">{GATEWAY.rpc.replace("https://", "")}</span>
              <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-gray-400">Updated {UPDATED}</span>
              <a
                href={GATEWAY.dashboard}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-primary px-4 py-1.5 font-semibold text-space-deep no-underline shadow-glow-sm transition-colors hover:bg-primary-400"
              >
                Get an API key
              </a>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="mx-auto flex max-w-[1400px] gap-10 px-8 py-10 max-sm:px-6 lg:px-12">
          {/* Mobile toggle */}
          <div className="fixed bottom-5 right-5 z-40 lg:hidden">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="rounded-full border border-white/15 bg-space-card/95 px-4 py-2.5 text-sm font-semibold text-white shadow-2xl backdrop-blur-md"
              aria-expanded={menuOpen}
            >
              {menuOpen ? "Close" : "Browse networks"}
            </button>
          </div>
          {menuOpen && (
            <div className="fixed inset-0 z-30 overflow-y-auto bg-space-deep/95 px-6 pb-24 pt-28 backdrop-blur-md lg:hidden">
              <ApiSidebar onNavigate={() => setMenuOpen(false)} />
            </div>
          )}

          <aside className="hidden w-64 shrink-0 lg:block">
            {/* direction:rtl on the scroll container puts the (green) scrollbar on
                the LEFT edge of the sidebar; the inner wrapper restores ltr text. */}
            <div className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto pl-3" style={{ direction: "rtl" }}>
              <div style={{ direction: "ltr" }}>
                <ApiSidebar />
              </div>
            </div>
          </aside>

          <main className="min-w-0 flex-1">
            <Outlet />
          </main>
        </div>

        <Footer />
      </div>
    </DemoKeyContext.Provider>
  );
};

export default ApiLayout;
